import React, { useState, createContext, useContext } from 'react';
import { Contact, Communication } from '../types/crm';
import { contacts as seedContacts, communications as seedCommunications } from '../utils/crmSeedData';
interface CRMContextType {
  contacts: Contact[];
  communications: Communication[];
  getContactsByCompanyId: (companyId: string) => Contact[];
  getContactById: (id: string) => Contact | undefined;
  addContact: (contact: Omit<Contact, 'id' | 'created_at' | 'updated_at'>) => void;
  updateContact: (id: string, updates: Partial<Contact>) => void;
  deleteContact: (id: string) => void;
  getCommunicationsByContactId: (contactId: string) => Communication[];
  addCommunication: (communication: Omit<Communication, 'id'>) => void;
}
const CRMContext = createContext<CRMContextType | undefined>(undefined);
export function CRMProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [contacts, setContacts] = useState<Contact[]>(seedContacts);
  const [communications, setCommunications] = useState<Communication[]>(seedCommunications);
  const getContactsByCompanyId = (companyId: string) => {
    return contacts.filter(contact => contact.company_id === companyId);
  };
  const getContactById = (id: string) => {
    return contacts.find(contact => contact.id === id);
  };
  const addContact = (contactData: Omit<Contact, 'id' | 'created_at' | 'updated_at'>) => {
    const newContact: Contact = {
      ...contactData,
      id: `contact-${Date.now()}`,
      created_at: new Date(),
      updated_at: new Date()
    };
    setContacts([...contacts, newContact]);
  };
  const updateContact = (id: string, updates: Partial<Contact>) => {
    setContacts(contacts.map(contact => contact.id === id ? {
      ...contact,
      ...updates,
      updated_at: new Date()
    } : contact));
  };
  const deleteContact = (id: string) => {
    setContacts(contacts.filter(contact => contact.id !== id));
    setCommunications(communications.filter(comm => comm.contact_id !== id));
  };
  const getCommunicationsByContactId = (contactId: string) => {
    return communications.filter(comm => comm.contact_id === contactId).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  };
  const addCommunication = (commData: Omit<Communication, 'id'>) => {
    const newComm: Communication = {
      ...commData,
      id: `comm-${Date.now()}`
    };
    setCommunications([...communications, newComm]);
  };
  const value: CRMContextType = {
    contacts,
    communications,
    getContactsByCompanyId,
    getContactById,
    addContact,
    updateContact,
    deleteContact,
    getCommunicationsByContactId,
    addCommunication
  };
  return <CRMContext.Provider value={value} data-id="element-387">{children}</CRMContext.Provider>;
}
export function useCRM(): CRMContextType {
  const context = useContext(CRMContext);
  if (!context) {
    throw new Error('useCRM must be used within a CRMProvider');
  }
  return context;
}